// Log button — the deal header's main action (record-page redesign QW3,
// 2026-10-13). An accent split button: the main half opens the notes
// composer in the kind this viewer used last; the chevron picks another
// kind (and remembers it). The page owns what "open the composer" means —
// this only calls onPick(kind) with one of LOG_KINDS' keys:
//   note · call · email · meeting · task · next_action
// The last kind is a per-viewer convenience in localStorage
// ('atlas.logKind'); unreadable storage just means "note".
// `dot` marks a kept composer draft (views/record_notes.jsx useRecordDraftFlag);
// `kinds` (keys) narrows the menu for a page that offers fewer kinds.

const LOG_KINDS = [
  { key: 'note',        label: 'Note',        main: 'Log note',    icon: (s) => <Icon.Doc size={s} /> },
  { key: 'call',        label: 'Call',        main: 'Log call',    icon: (s) => <Icon.Phone size={s} /> },
  { key: 'email',       label: 'Email',       main: 'Log email',   icon: (s) => <Icon.Mail size={s} /> },
  { key: 'meeting',     label: 'Meeting',     main: 'Log meeting', icon: (s) => <Icon.Users size={s} /> },
  { key: 'task',        label: 'Task',        main: 'Add task',    icon: (s) => <Icon.Check size={s} /> },
  { key: 'next_action', label: 'Next action', main: 'Next action', icon: (s) => <Icon.Calendar size={s} /> },
];
const LOG_KIND_PREF = 'atlas.logKind';

const readLogKind = () => {
  try {
    const v = localStorage.getItem(LOG_KIND_PREF);
    return LOG_KINDS.some((k) => k.key === v) ? v : 'note';
  } catch (_) { return 'note'; }
};
const writeLogKind = (key) => { try { localStorage.setItem(LOG_KIND_PREF, key); } catch (_) { /* private mode */ } };

const _noEscape = () => {};

const LogButton = ({ onPick, disabled = false, dot = false, kinds = null }) => {
  const [kind, setKind] = React.useState(readLogKind);
  // The open menu's position (fixed, under the button's right edge) | null.
  const [menu, setMenu] = React.useState(null);
  const wrapRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const open = !!menu;
  // Closing from the keyboard hands focus back to the chevron (the menu
  // lives in a portal — without this it would drop to the page body).
  const closeToChevron = () => {
    setMenu(null);
    const btns = wrapRef.current ? wrapRef.current.querySelectorAll('button') : [];
    if (btns[1]) btns[1].focus({ preventScroll: true });
  };
  // Escape closes it (stack-aware: only the top overlay reacts).
  (window.useEscapeKey || _noEscape)(open, closeToChevron);
  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      setMenu(null);
    };
    const onResize = () => setMenu(null);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);
  // Keyboard: the menu takes focus on the current kind when it opens.
  React.useEffect(() => {
    if (!open || !menuRef.current) return;
    const cur = menuRef.current.querySelector('[data-current="1"]') || menuRef.current.querySelector('button');
    if (cur) cur.focus({ preventScroll: true });
  }, [open]);

  const avail = kinds ? LOG_KINDS.filter((k) => kinds.includes(k.key)) : LOG_KINDS;
  const current = avail.find((k) => k.key === kind) || avail[0];
  const pick = (key) => {
    setMenu(null);
    setKind(key);
    writeLogKind(key);
    if (onPick) onPick(key);
  };
  const toggleMenu = () => {
    if (open) { setMenu(null); return; }
    const r = wrapRef.current && wrapRef.current.getBoundingClientRect();
    if (!r) return;
    setMenu({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
  };
  const onMenuKey = (e) => {
    if (e.key === 'Tab') { e.preventDefault(); closeToChevron(); return; }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(menuRef.current ? menuRef.current.querySelectorAll('button') : []);
    if (!items.length) return;
    const i = items.indexOf(document.activeElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[next].focus();
  };

  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'stretch', flexShrink: 0 }}>
      {dot && (
        <span aria-label="Draft kept" role="img"
          style={{ position: 'absolute', top: -3, left: -3, width: 8, height: 8, borderRadius: 999, zIndex: 1,
            background: 'var(--amber-fg)', boxShadow: '0 0 0 2px var(--surface)', pointerEvents: 'none' }} />
      )}
      <Btn size="sm" variant="accent" icon={current.icon(12)} disabled={disabled}
        onClick={() => pick(current.key)}
        style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>{current.main}</Btn>
      <span aria-haspopup="menu" aria-expanded={open} style={{ display: 'inline-flex' }}>
        <Btn size="sm" variant="accent" icon={<Icon.ChevronD size={12} />} disabled={disabled}
          title="Log something else"
          onClick={toggleMenu}
          style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: '1px solid rgba(255,255,255,0.35)', padding: '5px 6px' }} />
      </span>
      {menu && ReactDOM.createPortal(
        <div ref={menuRef} role="menu" onKeyDown={onMenuKey}
          style={{
            position: 'fixed', top: menu.top, right: menu.right, zIndex: 400, minWidth: 168,
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
            boxShadow: 'var(--shadow-lg)', padding: 4,
          }}>
          {avail.map((k) => {
            const isCur = k.key === kind;
            return (
              <button key={k.key} type="button" role="menuitem" data-current={isCur ? '1' : undefined}
                onClick={() => pick(k.key)}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                onFocus={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
                onBlur={(e) => { e.currentTarget.style.background = 'transparent'; }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '6px 8px',
                  border: 'none', borderRadius: 5, background: 'transparent', cursor: 'pointer',
                  fontSize: 12.5, fontFamily: 'inherit', textAlign: 'left', outline: 'none',
                  color: 'var(--text)', fontWeight: isCur ? 600 : 500,
                }}>
                <span style={{ display: 'inline-flex', color: 'var(--text-3)', flexShrink: 0 }}>{k.icon(12)}</span>
                <span style={{ flex: 1 }}>{k.label}</span>
                {isCur && <span style={{ display: 'inline-flex', color: 'var(--accent-text)', flexShrink: 0 }}><Icon.Check size={11} /></span>}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </span>
  );
};

window.LogButton = LogButton;
